import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, message, Modal, Space, Table, Tooltip } from 'antd';
import { DollarOutlined, FilePdfOutlined } from '@ant-design/icons';
import moment from 'moment';

import client from '../../../../api/api';
import { getSessionUserData } from '../../../../libs/user';
import useSearchParams from '../../../Global/Antd/Table/useSearchParams';
import SearchColumn from '../../../Global/Antd/Table/searchColumn';
import useDocumentsModal from './useDocumentsModal';
import { SettlementAdvance } from '../../../SettlementAdvance';

const browserLocale = navigator.language || 'es-AR';

const currencyFormatter = new Intl.NumberFormat(browserLocale, {
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'narrowSymbol',
});

const CarrierRecordBillingTable2 = ({ advanceEnabled }) => {
  const [invoices, setInvoices] = useState();
  const [loading, setIsLoading] = useState(false);
  const [paginationConfiguration, setPaginationConfiguration] = useState();
  const [order, setOrder] = useState({});
  const [searchParams, setSearchParams] = useState([]);
  const [paginationParams, setPaginationParams] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const [queryParams, handleColumnSearch, handleResetColumnSearch, orderParams] = useSearchParams(
    searchParams,
    setSearchParams
  );
  const { modal: documentsModal, openModal: openDocumentsModal } = useDocumentsModal();

  const [billingIdSelected, setBillingIdSelected] = useState('');
  const [invoiceNumberSelected, setInvoiceNumberSelected] = useState('');
  const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);

  const closeAdvanceModal = () => {
    setIsAdvanceModalOpen(false);
    setBillingIdSelected('');
    setInvoiceNumberSelected('');
  };

  const columns = [
    {
      title: 'Acciones',
      align: 'right',
      key: 'acciones',
      render: record => {
        const { canAskAdvance } = record;
        return (
          <Space size="small">
            {advanceEnabled && canAskAdvance ? (
              <Tooltip title="Solicitar adelanto">
                <Button
                  type="text"
                  icon={<DollarOutlined style={{ color: '#6495ed', fontSize: 20 }} />}
                  onClick={() => {
                    setBillingIdSelected(record.id);
                    setInvoiceNumberSelected(record.invoiceNumber);
                    setIsAdvanceModalOpen(true);
                  }}
                />
              </Tooltip>
            ) : null}
            <Tooltip title="Ver documentos">
              <Button
                type="text"
                icon={<FilePdfOutlined style={{ color: '#6495ed', fontSize: 20 }} />}
                onClick={() => openDocumentsModal(record.invoiceNumber)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Factura',
      align: 'center',
      key: 'invoiceNumber',
      sorter: true,
      render: record => {
        return (
          <div>
            {record.invoiceType} {record.invoiceNumber}
          </div>
        );
      },
      ...SearchColumn('invoiceNumber', handleColumnSearch, handleResetColumnSearch),
    },
    {
      title: 'Vencimiento',
      align: 'center',
      key: 'dueDate',
      dataIndex: 'dueDate',
      sorter: true,
      render: value => {
        return value
          ? moment(value)
              .locale(browserLocale)
              .format('DD-MM-YYYY')
          : '';
      },
    },
    {
      title: 'Pago',
      align: 'center',
      key: 'paymentDay',
      sorter: true,
      render: record => {
        const { paymentDay } = record;
        return paymentDay
          ? moment(paymentDay)
              .locale(browserLocale)
              .format('DD-MM-YYYY')
          : '';
      },
    },
    {
      title: 'Ajuste',
      align: 'right',
      key: 'adjustmentAmount',
      dataIndex: 'adjustmentAmount',
      render: value => currencyFormatter.format(value),
    },
    {
      title: 'Neto',
      align: 'right',
      key: 'netAmount',
      dataIndex: 'netAmount',
      sorter: true,
      render: value => currencyFormatter.format(value),
    },
    {
      title: 'IVA',
      align: 'right',
      key: 'ivaAmount',
      dataIndex: 'ivaAmount',
      render: value => currencyFormatter.format(value),
    },
    {
      title: 'Líquido Producto',
      align: 'right',
      key: 'liquidProductAmount',
      dataIndex: 'liquidProductAmount',
      render: value => currencyFormatter.format(value),
    },
    {
      title: 'Gasoil',
      align: 'right',
      key: 'gasoilAmount',
      dataIndex: 'gasoilAmount',
      sorter: true,
      render: value => currencyFormatter.format(value),
    },
    {
      title: 'Adelanto Bamba',
      align: 'right',
      key: 'bambaAmount',
      dataIndex: 'bambaAmount',
      sorter: true,
      render: value => currencyFormatter.format(value),
    },
    {
      title: 'Final',
      align: 'right',
      key: 'finalAmount',
      dataIndex: 'finalAmount',
      sorter: true,
      render: value => currencyFormatter.format(value),
    },
  ];

  const fetchInvoices = useCallback(async () => {
    try {
      setIsLoading(true);
      const t = queryParams(order, paginationParams);
      const res = await client.endpoints.carrier.getInvoices2(
        // eslint-disable-next-line no-underscore-dangle
        { ownerId: getSessionUserData().data._id, ...t }
      );
      setPaginationConfiguration({
        position: ['bottomLeft'],
        total: res.data.count,
        showTotal: total => `Total de ${total} items`,
        pageSize: res.data.limit,
        pageSizeOptions: ['10', '20', '50'],
        current: res.data.page,
        showSizeChanger: true,
      });
      setInvoices(res.data.docs);
    } catch (error) {
      console.error(error);
      message.error('Error al cargar las facturas');
    } finally {
      setIsLoading(false);
    }
  }, [order, paginationParams]);

  useEffect(() => {
    fetchInvoices();
  }, [paginationParams]);

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.column) {
      setOrder(orderParams(sorter));
    }
    setPaginationParams({
      currentPage: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  return (
    <>
      <Table
        style={{ paddingLeft: '8px' }}
        columns={columns}
        dataSource={invoices}
        rowKey="id"
        loading={loading}
        pagination={paginationConfiguration}
        onChange={handleTableChange}
      />
      {documentsModal}

      <Modal
        visible={isAdvanceModalOpen}
        onCancel={closeAdvanceModal}
        title={`Factura ${invoiceNumberSelected}`}
        destroyOnClose
        footer={null}
        width={600}
      >
        <SettlementAdvance billingId={billingIdSelected} />
      </Modal>
    </>
  );
};
CarrierRecordBillingTable2.propTypes = {
  advanceEnabled: PropTypes.bool.isRequired,
};

export default CarrierRecordBillingTable2;
